import React from 'react';
import type {
  EsgDashboardByBranchResponse,
  EsgDashboardMonthlyResponse,
  EsgDashboardOverviewResponse,
} from '../../types/esg-dashboard.type';
import type { WasteTypeWithMaterials } from '../../types/dashboard.type';
import {
  deriveIncomingWasteTotalKg,
  deriveTotalUtilityCostSar,
  buildGri306CoverageRows,
} from '../../utils/reportDerivedMetrics';
import { formatTco2e } from '../../utils/carbonAvoidance.util';
import { HSE_TERMS } from '../../constants/hseTerms';
import {
  formatCount,
  formatHours,
  formatKg,
  formatPercent,
  formatSar,
  safeNumber,
} from '../../utils/reportFormatters';
import {
  ReportContentHeader,
  ReportCoverPage,
  ReportPageShell,
} from './ReportChrome';
import { reportMuted } from '../../constants/reportTheme';
import {
  isEsgReportSectionEnabled,
  type EsgReportSectionId,
} from '../../constants/esgReportCustomize.catalog';

export type ReportPdfVariant = 'general' | 'waste_circularity_gri306';

export interface ReportPdfTemplateProps {
  variant: ReportPdfVariant;
  monthly: EsgDashboardMonthlyResponse | null;
  overview: EsgDashboardOverviewResponse | null;
  byBranch: EsgDashboardByBranchResponse | null;
  organizationName: string;
  organizationLogoSrc: string | null;
  periodLabel: string;
  generatedLabel: string;
  containerId?: string;
  includeSections?: readonly EsgReportSectionId[];
}

function pushPage(
  pages: React.ReactNode[],
  ...nodes: Array<React.ReactNode | false | null | undefined>
) {
  const kids = nodes.filter(Boolean);
  if (kids.length === 0) return;
  pages.push(<>{kids}</>);
}

function sectionOn(
  id: EsgReportSectionId,
  includeSections?: readonly EsgReportSectionId[] | null,
) {
  return isEsgReportSectionEnabled(id, includeSections);
}

const muted: React.CSSProperties = reportMuted;
const cardGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
  marginBottom: 8,
};
const cardStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 4,
  padding: '12px 14px',
  background: '#fafbfc',
};
const cardLabel: React.CSSProperties = {
  fontSize: '9pt',
  color: '#64748b',
  marginBottom: 6,
  fontWeight: 500,
};
const cardValue: React.CSSProperties = {
  fontSize: '14pt',
  fontWeight: 600,
  color: '#0f172a',
};

function Section({
  title,
  intro,
  children,
}: {
  title: string;
  intro?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="esg-report-avoid-break" style={{ marginBottom: 22 }}>
      <h2
        style={{
          fontSize: '11pt',
          fontWeight: 600,
          color: '#1e293b',
          borderBottom: '1px solid #e2e8f0',
          paddingBottom: 8,
          marginBottom: intro ? 8 : 12,
        }}
      >
        {title}
      </h2>
      {intro ? (
        <p style={{ ...muted, marginBottom: 12, marginTop: 0, lineHeight: 1.5 }}>{intro}</p>
      ) : null}
      {children}
    </section>
  );
}

function KeyValueTable({ rows }: { rows: { label: string; value: string }[] }) {
  return (
    <table className="esg-report-table esg-report-avoid-break">
      <tbody>
        {rows.map((r) => (
          <tr key={r.label}>
            <td style={{ width: '42%', fontWeight: 500, color: '#475569' }}>{r.label}</td>
            <td>{r.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function formatOperationalScalar(
  monthly: EsgDashboardMonthlyResponse,
  key: keyof EsgDashboardMonthlyResponse
): string {
  const v = monthly[key];
  const n = safeNumber(v);
  if (n !== null) {
    const ks = key.toString();
    if (
      ks.includes('rate') ||
      ks.includes('efficiency') ||
      ks.includes('utilization') ||
      ks.includes('score') ||
      ks.includes('percentage') ||
      ks === 'full_percentage'
    ) {
      return formatPercent(n);
    }
    return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  if (Array.isArray(v)) return `${v.length} record(s)`;
  return 'Not available';
}

/** Monitoring “quality” KPIs: backend often sends 0 when data is absent — show Unavailable instead. */
function formatMonitoringQualityMetric(
  monthly: EsgDashboardMonthlyResponse,
  key:
    | 'average_container_fill_rate'
    | 'quality_compliance_rate'
    | 'rejected_waste_percentage'
    | 'waste_quality_rating'
): string {
  const v = monthly[key];
  if (v === null || v === undefined) return 'Unavailable';
  const n = safeNumber(v);
  if (n === null || n === 0) return 'Unavailable';
  return formatOperationalScalar(monthly, key);
}

const HIDDEN_MONITORING_METRICS = new Set([
  'Quality compliance rate',
  'Rejected waste percentage',
  'Waste quality rating',
]);

function recycledBreakdownRows(
  data: WasteTypeWithMaterials[]
): { waste: string; kg: string; materials: string }[] {
  return data.map((row) => ({
    waste: row.waste_type || '—',
    kg: formatKg(row.total_kg),
    materials:
      row.materials?.map((m) => `${m.material_type}: ${formatKg(m.amount)}`).join('; ') || '—',
  }));
}

function ExecCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="esg-report-avoid-break" style={cardStyle}>
      <div style={cardLabel}>{label}</div>
      <div style={cardValue}>{value}</div>
    </div>
  );
}

function MethodologySectionGeneral() {
  return (
    <Section
      title="Methodology"
      intro="Brief notes on data scope and how to interpret this export responsibly."
    >
      <ul style={{ ...muted, paddingLeft: 18, margin: 0 }}>
        <li style={{ marginBottom: 6 }}>
          Figures come from your monthly ESG dashboard view for the selected organization and month.
        </li>
        <li style={{ marginBottom: 6 }}>
          This report does not claim GRI, ISO, or regulatory compliance — only what the platform currently displays.
        </li>
        <li style={{ marginBottom: 6 }}>
          Display rounding may differ slightly from underlying stored values.
        </li>
        <li style={{ marginBottom: 6 }}>
          Waste collected via platform is the mass of completed pickups dated by order creation. It includes loads sent
          to disposal destinations and loads later rejected, so it is not a GRI 306-4 “diverted from disposal” figure.
        </li>
        <li style={{ marginBottom: 6 }}>
          Pickup completion rate = completed mass ÷ mass of all pickup orders in the month, including pending and
          cancelled orders. It is not a waste diversion rate.
        </li>
        <li style={{ marginBottom: 6 }}>
          Avoided emissions are estimated against a baseline treatment (ISO 14064-2-style project versus baseline).
          In line with the GHG Protocol and IFRS S2 they are reported separately from Scope 1, 2 and 3, are never
          deducted from them, and are not offsets or carbon credits.
        </li>
      </ul>
    </Section>
  );
}

function MethodologySectionGri() {
  return (
    <Section
      title="Methodology and assumptions"
      intro="Context for waste-focused disclosures and the meaning of “mapped to GRI 306 themes”."
    >
      <ul style={{ ...muted, paddingLeft: 18, margin: 0 }}>
        <li style={{ marginBottom: 6 }}>
          Numbers match the reporting month and organization shown in your dashboard filters.
        </li>
        <li style={{ marginBottom: 6 }}>
          Branch tables reflect site-level monthly figures; zeros may mean no activity or no reporting.
        </li>
        <li style={{ marginBottom: 6 }}>
          “Mapped to GRI 306 themes” refers to thematic overlap with GRI 306 topics — not assurance or formal GRI
          reporting.
        </li>
        <li style={{ marginBottom: 6 }}>
          Recovery and disposal operations (GRI 306-4 / 306-5) and a recycling-only share (SASB IF-WM) are not
          recorded per load yet, so no recycling or diversion-from-disposal figure is stated.
        </li>
        <li style={{ marginBottom: 6 }}>
          MWAN chain-of-custody evidence (licensed transporter and recycler, manifests) is not included in this export.
        </li>
      </ul>
    </Section>
  );
}

const GENERAL_TITLE = 'General ESG Performance Report';
const GRI_TITLE = 'Waste & Circularity Report — mapped to GRI 306 themes';

function formatTco2eValue(value: unknown): string {
  const n = safeNumber(value);
  if (n === null) return 'Not available';
  return `${formatTco2e(n)} tCO2e`;
}

function GeneralReport({
  monthly,
  byBranch,
  organizationName,
  organizationLogoSrc,
  periodLabel,
  generatedLabel,
  includeSections,
}: Omit<ReportPdfTemplateProps, 'variant' | 'overview' | 'containerId'>) {
  const incomingKg = deriveIncomingWasteTotalKg(monthly);
  const utilityTotal = deriveTotalUtilityCostSar(monthly);
  const branches = byBranch?.branches ?? [];

  const execGrid = (
    <div style={cardGrid}>
      <ExecCard
        label="Total waste handled (platform-recorded incoming)"
        value={incomingKg !== null ? formatKg(incomingKg) : 'Not available'}
      />
      <ExecCard label="Waste collected via platform" value={formatKg(monthly?.total_waste_diverted_kg)} />
      <ExecCard label="Pickup completion rate (by mass)" value={formatPercent(monthly?.waste_diversion_rate_percent)} />
      <ExecCard
        label="Avoided emissions (outside the GHG inventory)"
        value={formatTco2eValue(monthly?.total_avoided_tco2e)}
      />
      <ExecCard label="Scope 1 emissions" value={formatTco2eValue(monthly?.scope_emissions?.scope_1_tco2e)} />
      <ExecCard label="Scope 2 emissions" value={formatTco2eValue(monthly?.scope_emissions?.scope_2_tco2e)} />
      <ExecCard label="Total utility cost" value={utilityTotal !== null ? formatSar(utilityTotal) : 'Not available'} />
      <ExecCard
        label="Total training hours"
        value={
          monthly?.training_hours_total === null || monthly?.training_hours_total === undefined
            ? 'Not available'
            : formatHours(monthly.training_hours_total)
        }
      />
      <ExecCard
        label="Recordable work injuries"
        value={monthly?.hse && monthly.hse.weeks_reported > 0 ? formatCount(monthly.hse.recordable_injuries) : 'Not reported'}
      />
    </div>
  );

  const breakdown = monthly?.recycled_waste_breakdown_by_type ?? [];

  const wasteMetricsRows = monthly
    ? [
        { label: 'Waste collected via platform', value: formatKg(monthly.total_waste_diverted_kg) },
        { label: 'Pickup completion rate (by mass)', value: formatPercent(monthly.waste_diversion_rate_percent) },
        { label: 'Fill-level reports', value: formatCount(monthly.fill_level_report_count) },
      ]
    : [];

  const utilityRows = monthly
    ? (monthly.scope_emissions?.activity_lines ?? []).map((line) => ({
        label: line.label,
        value: `${line.consumption.toLocaleString('en-US')} ${line.canonical_unit} — ${formatSar(line.amount_sar)}`,
      }))
    : [];

  const emissionsRows = monthly
    ? (monthly.scope_emissions?.activity_lines ?? [])
        .filter((line) => line.ghg_scope !== null)
        .map((line) => ({
          label: `${line.label} · Scope ${line.ghg_scope}`,
          value: line.missing_factor
            ? 'Incomplete — factor not configured'
            : `${formatTco2eValue(line.emissions_tco2e)} · factor ${line.factor_version_label ?? 'not available'}`,
        }))
    : [];

  const monitoringRows = monthly
    ? [
        {
          label: 'Average container fill rate',
          value: formatMonitoringQualityMetric(monthly, 'average_container_fill_rate'),
        },
        {
          label: 'Quality compliance rate',
          value: formatMonitoringQualityMetric(monthly, 'quality_compliance_rate'),
        },
        {
          label: 'Rejected waste percentage',
          value: formatMonitoringQualityMetric(monthly, 'rejected_waste_percentage'),
        },
        {
          label: 'Waste quality rating',
          value: formatMonitoringQualityMetric(monthly, 'waste_quality_rating'),
        },
      ].filter((row) => !HIDDEN_MONITORING_METRICS.has(row.label) && row.value !== 'Unavailable')
    : [];

  const hse = monthly?.hse;
  const rate = (value: number | null | undefined, basis: string) =>
    value === null || value === undefined ? 'Not available (no hours worked reported)' : `${value.toFixed(2)} ${basis}`;
  const hseRows =
    hse && hse.weeks_reported > 0
      ? [
          { label: 'Recordable work injuries', value: formatCount(hse.recordable_injuries) },
          { label: 'Lost-time injuries', value: formatCount(hse.lost_time_injuries) },
          { label: 'Fatalities', value: formatCount(hse.fatalities) },
          { label: 'Lost days', value: formatCount(hse.lost_days) },
          { label: 'All reported incidents', value: formatCount(hse.total_incidents) },
          { label: 'Hours worked', value: hse.hours_worked === null ? 'Not reported' : formatHours(hse.hours_worked) },
          { label: 'Average workforce (headcount)', value: hse.headcount === null ? 'Not reported' : formatCount(hse.headcount) },
          { label: 'TRIR', value: rate(hse.trir, 'per 200,000 h') },
          { label: 'LTIFR', value: rate(hse.ltifr, 'per 1,000,000 h') },
          { label: 'Lost-day rate', value: rate(hse.lost_day_rate, 'per 200,000 h') },
          {
            label: 'Weeks reported / submitted',
            value: `${formatCount(hse.weeks_reported)} / ${formatCount(hse.weeks_submitted)}`,
          },
        ]
      : [];
  const hseTermRows = hse
    ? HSE_TERMS.map((term) => ({ label: term.label, value: formatCount(hse.counts[term.key] ?? 0) }))
    : [];

  const socialRows = monthly
    ? [
        {
          label: 'Training hours (period)',
          value:
            monthly.training_hours_total === null || monthly.training_hours_total === undefined
              ? 'Not available'
              : formatHours(monthly.training_hours_total),
        },
      ]
    : [];

  const pageBodies: React.ReactNode[] = [];
  const on = (id: EsgReportSectionId) => sectionOn(id, includeSections);

  pageBodies.push(
    <ReportCoverPage
      reportTitle={GENERAL_TITLE}
      organizationName={organizationName}
      organizationLogoSrc={organizationLogoSrc}
      periodLabel={periodLabel}
      generatedLabel={generatedLabel}
    />
  );

  if (on('general-exec-summary')) {
    pageBodies.push(
      <>
        <ReportContentHeader
          title={GENERAL_TITLE}
          organizationName={organizationName}
          organizationLogoSrc={organizationLogoSrc}
          periodLabel={periodLabel}
          generatedLabel={generatedLabel}
          showSummaryNote
        />
        <Section
          title="Executive summary"
          intro="Key waste, carbon, utility, and training totals for this period in one view."
        >
          {execGrid}
        </Section>
      </>
    );
  }

  if (monthly) {
    pushPage(
      pageBodies,
      on('general-waste-metrics') ? (
        <Section
          title="Waste metrics"
          intro="Collected mass, pickup completion and reporting counts for the month; composition table appears when detail exists."
        >
          <KeyValueTable rows={wasteMetricsRows} />
          {breakdown.length > 0 ? (
            <>
              <p style={{ ...muted, margin: '12px 0 8px' }}>Collected waste composition (by type)</p>
              <table className="esg-report-table">
                <thead>
                  <tr>
                    <th>Waste type</th>
                    <th>Total</th>
                    <th>Materials</th>
                  </tr>
                </thead>
                <tbody>
                  {recycledBreakdownRows(breakdown).map((r) => (
                    <tr key={r.waste}>
                      <td>{r.waste}</td>
                      <td>{r.kg}</td>
                      <td style={{ fontSize: '8pt' }}>{r.materials}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </Section>
      ) : null,
      on('general-utility-metrics') ? (
        <Section
          title="Utility metrics"
          intro="Recorded consumption and spend by utility type for this period."
        >
          <KeyValueTable rows={utilityRows} />
        </Section>
      ) : null,
      on('general-scope-emissions') ? (
        <Section
          title="Scope 1 and Scope 2 emissions"
          intro="Location-based utility emissions calculated from the factor versions recorded for this reporting month."
        >
          <KeyValueTable
            rows={[
              {
                label: 'Scope 1 total',
                value: formatTco2eValue(monthly.scope_emissions?.scope_1_tco2e),
              },
              {
                label: 'Scope 2 total',
                value: formatTco2eValue(monthly.scope_emissions?.scope_2_tco2e),
              },
              ...emissionsRows,
            ]}
          />
        </Section>
      ) : null,
    );
  }

  if (monthly) {
    pushPage(
      pageBodies,
      on('general-monitoring-metrics') && monitoringRows.length > 0 ? (
        <Section
          title="Monitoring metrics"
          intro="Operational quality signals from your monthly dashboard for bins, rejects, and ratings."
        >
          <KeyValueTable rows={monitoringRows} />
        </Section>
      ) : null,
      on('general-social-training') ? (
        <Section
          title="Social / training metrics"
          intro="Training effort captured for the month; session history stays under Employee Training."
        >
          <KeyValueTable rows={socialRows} />
          <p style={{ ...muted, marginTop: 8 }}>
            Detailed sessions live in the Employee Training area of Recyclee ESG.
          </p>
        </Section>
      ) : null,
      on('general-social-hse') ? (
        <Section
          title="Health & safety"
          intro="Work-related injuries and incidents from the weekly Health & Safety reports of every branch (GRI 403-9). Rates use only weeks that report hours worked."
        >
          {hseRows.length > 0 ? (
            <>
              <KeyValueTable rows={hseRows} />
              <p style={{ ...muted, marginTop: 12, marginBottom: 6 }}>Incidents by type</p>
              <KeyValueTable rows={hseTermRows} />
            </>
          ) : (
            <p style={muted}>No Health &amp; Safety weeks were reported for this period.</p>
          )}
        </Section>
      ) : null,
      on('general-branches') && branches.length > 0 ? (
        <Section
          title="Branches Performance"
          intro="Waste and carbon performance by collection site for this month. Utility consumption is reported separately under Utility metrics."
        >
          <table className="esg-report-table">
            <thead>
              <tr>
                <th>Branch</th>
                <th>Collected</th>
                <th>Completion rate</th>
                <th>Avoided emissions</th>
                <th>Fill reports</th>
              </tr>
            </thead>
            <tbody>
              {branches.map((b) => (
                <tr key={b.collection_site_id}>
                  <td>{b.collection_site_name || b.collection_site_id}</td>
                  <td>{formatKg(b.total_waste_diverted_kg)}</td>
                  <td>{formatPercent(b.waste_diversion_rate_percent)}</td>
                  <td>{formatTco2eValue(b.total_avoided_tco2e)}</td>
                  <td>{formatCount(b.fill_level_report_count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      ) : null,
    );
  }

  if (on('general-methodology')) {
    pageBodies.push(<MethodologySectionGeneral />);
  }

  const totalPages = pageBodies.length;

  return (
    <>
      {pageBodies.map((body, i) => (
        <ReportPageShell
          key={i}
          periodLabel={periodLabel}
          generatedLabel={generatedLabel}
          pageIndex={i + 1}
          totalPages={totalPages}
        >
          {body}
        </ReportPageShell>
      ))}
    </>
  );
}

function WasteCircularityReport({
  monthly,
  byBranch,
  organizationName,
  organizationLogoSrc,
  periodLabel,
  generatedLabel,
  includeSections,
}: Omit<ReportPdfTemplateProps, 'variant' | 'overview' | 'containerId'>) {
  const incomingKg = deriveIncomingWasteTotalKg(monthly);
  const griRows = buildGri306CoverageRows(monthly, byBranch);

  const branches = byBranch?.branches ?? [];
  const unassigned = byBranch?.unassigned_waste ?? null;
  const breakdown = monthly?.recycled_waste_breakdown_by_type ?? [];

  const execGrid = (
    <div style={cardGrid}>
      <ExecCard
        label="Platform-recorded waste handled (incoming breakdown total)"
        value={incomingKg !== null ? formatKg(incomingKg) : 'Not available'}
      />
      <ExecCard label="Waste collected via platform" value={formatKg(monthly?.total_waste_diverted_kg)} />
      <ExecCard label="Pickup completion rate (by mass)" value={formatPercent(monthly?.waste_diversion_rate_percent)} />
    </div>
  );

  const pageBodies: React.ReactNode[] = [];
  const on = (id: EsgReportSectionId) => sectionOn(id, includeSections);

  pageBodies.push(
    <ReportCoverPage
      reportTitle={GRI_TITLE}
      organizationName={organizationName}
      organizationLogoSrc={organizationLogoSrc}
      periodLabel={periodLabel}
      generatedLabel={generatedLabel}
    />
  );

  if (on('waste-exec-summary')) {
    pageBodies.push(
      <>
        <ReportContentHeader
          title={GRI_TITLE}
          organizationName={organizationName}
          organizationLogoSrc={organizationLogoSrc}
          periodLabel={periodLabel}
          generatedLabel={generatedLabel}
          showSummaryNote
        />
        <p style={{ ...muted, marginTop: -8, marginBottom: 16 }}>
          Waste-focused disclosure mapped to GRI 306 themes — not a statement of GRI conformance or external assurance.
        </p>
        <Section
          title="Executive summary"
          intro="Headline collection and completion metrics for stakeholders reviewing circularity performance."
        >
          {execGrid}
        </Section>
      </>
    );
  }

  const midSections: React.ReactNode[] = [];
  if (monthly && on('waste-handled')) {
    midSections.push(
      <Section
        key="wh"
        title="Waste handled"
        intro="Incoming totals where recorded; notes explain limits when breakdown detail is missing."
      >
        <KeyValueTable
          rows={[
            {
              label: 'Incoming breakdown total (sum of amounts)',
              value: incomingKg !== null ? formatKg(incomingKg) : 'Not available',
            },
            {
              label: 'Notes',
              value:
                'If incoming breakdown is empty, handled mass cannot be derived from this export alone.',
            },
          ]}
        />
      </Section>
    );
  }

  if (breakdown.length > 0 && on('waste-composition')) {
    midSections.push(
      <Section
        key="wc"
        title="Waste composition breakdown"
        intro="Collected waste split by type and material when your data includes that breakdown."
      >
        <table className="esg-report-table">
          <thead>
            <tr>
              <th>Waste type</th>
              <th>Total collected (kg)</th>
              <th>Materials</th>
            </tr>
          </thead>
          <tbody>
            {recycledBreakdownRows(breakdown).map((r) => (
              <tr key={r.waste}>
                <td>{r.waste}</td>
                <td>{r.kg}</td>
                <td style={{ fontSize: '8pt' }}>{r.materials}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    );
  }

  if (monthly && on('waste-diverted-recycled')) {
    midSections.push(
      <Section
        key="wd"
        title="Waste collected"
        intro="Month-level collected mass and pickup completion rate side by side."
      >
        <KeyValueTable
          rows={[
            { label: 'Waste collected via platform', value: formatKg(monthly.total_waste_diverted_kg) },
            { label: 'Pickup completion rate (by mass)', value: formatPercent(monthly.waste_diversion_rate_percent) },
          ]}
        />
      </Section>
    );
  }

  if (midSections.length > 0) {
    pageBodies.push(<>{midSections}</>);
  }

  const branchTable =
    branches.length > 0 && on('waste-branch-table') ? (
      <Section
        title="Branch waste performance"
        intro="Each branch’s collected mass, pickup completion rate, and fill-level reporting counts."
      >
        <table className="esg-report-table">
          <thead>
            <tr>
              <th>Branch</th>
              <th>Collected</th>
              <th>Completion rate</th>
              <th>Fill reports</th>
            </tr>
          </thead>
          <tbody>
            {branches.map((b) => (
              <tr key={b.collection_site_id}>
                <td>{b.collection_site_name || b.collection_site_id}</td>
                <td>{formatKg(b.total_waste_diverted_kg)}</td>
                <td>{formatPercent(b.waste_diversion_rate_percent)}</td>
                <td>{formatCount(b.fill_level_report_count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    ) : null;

  const unassignedBlock =
    unassigned && on('waste-unassigned') ? (
      <Section
        title="Unassigned waste"
        intro="Aggregates not mapped to a named branch when your export includes an unassigned bucket."
      >
        <table className="esg-report-table">
          <tbody>
            <tr>
              <td style={{ fontWeight: 600 }}>Unassigned</td>
              <td>{formatKg(unassigned.total_waste_diverted_kg)} collected</td>
              <td>{formatPercent(unassigned.waste_diversion_rate_percent)}</td>
            </tr>
          </tbody>
        </table>
      </Section>
    ) : null;

  const disposalSection = on('waste-disposal-disclaimer') ? (
    <Section
      title="Waste directed to disposal"
      intro="Explains that landfill or disposal tonnage is not split out as its own figure here."
    >
      <p style={{ ...muted, margin: 0 }}>
        Disposal or landfill mass (GRI 306-5) is not tracked as a separate line item in this export. Do not infer
        disposal totals from collected figures alone.
      </p>
    </Section>
  ) : null;

  pushPage(pageBodies, branchTable, unassignedBlock, disposalSection);

  pushPage(
    pageBodies,
    on('waste-gri306-coverage') ? (
      <Section
        title="GRI 306 coverage mapping"
        intro="Topic-by-topic view of what this export supports versus known gaps in waste disclosure."
      >
        <table className="esg-report-table">
          <thead>
            <tr>
              <th style={{ width: '28%' }}>Topic</th>
              <th>What we show</th>
              <th>Gap or caveat</th>
            </tr>
          </thead>
          <tbody>
            {griRows.map((r) => (
              <tr key={r.topic}>
                <td style={{ fontWeight: 600 }}>{r.topic}</td>
                <td style={{ fontSize: '8pt' }}>{r.evidence}</td>
                <td style={{ fontSize: '8pt' }}>{r.gap}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    ) : null,
    on('waste-methodology') ? <MethodologySectionGri /> : null,
  );

  const totalPages = pageBodies.length;

  return (
    <>
      {pageBodies.map((body, i) => (
        <ReportPageShell
          key={i}
          periodLabel={periodLabel}
          generatedLabel={generatedLabel}
          pageIndex={i + 1}
          totalPages={totalPages}
        >
          {body}
        </ReportPageShell>
      ))}
    </>
  );
}

export function ReportPdfTemplate(props: ReportPdfTemplateProps) {
  const {
    variant,
    monthly,
    byBranch,
    organizationName,
    organizationLogoSrc,
    periodLabel,
    generatedLabel,
    overview: _overview,
    containerId = 'esg-report-print-root',
    includeSections,
  } = props;

  return (
    <div
      id={containerId}
      className="esg-report-screen-root esg-report-pdf-capture-root"
      dir="ltr"
      lang="en"
      aria-hidden
    >
      {variant === 'general' ? (
        <GeneralReport
          monthly={monthly}
          byBranch={byBranch}
          organizationName={organizationName}
          organizationLogoSrc={organizationLogoSrc}
          periodLabel={periodLabel}
          generatedLabel={generatedLabel}
          includeSections={includeSections}
        />
      ) : (
        <WasteCircularityReport
          monthly={monthly}
          byBranch={byBranch}
          organizationName={organizationName}
          organizationLogoSrc={organizationLogoSrc}
          periodLabel={periodLabel}
          generatedLabel={generatedLabel}
          includeSections={includeSections}
        />
      )}
    </div>
  );
}
